import { createContext, useContext, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useUx } from "@/lib/UxProvider";
import { useAuth } from "@/context/AuthContext";
import { applyBrandPrimary, clearBrandPrimary, LOGIN_BRAND_KEY } from "@/lib/brandColor";

const FALLBACK = {
  company_name: "REASON RetailOS™",
  logo_data_uri: "",
  primary_color: "#0F766E",
  receipt_footer: "Thank you for shopping with us!",
  show_logo_pos: true,
  show_logo_reports: true,
  default_language: "en",
  onboarding_completed: false,
  is_custom: false,
};

const BrandingCtx = createContext(FALLBACK);

export function BrandingProvider({ children }) {
  const { prefs, update } = useUx();
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ["branding", user?.id || user?._id || "anon"],
    queryFn: async () => (await api.get("/branding")).data,
    staleTime: 60_000,
    retry: false,
    enabled: !!user,
  });

  // Apply the tenant's default language for users who haven't explicitly picked one.
  useEffect(() => {
    const def = data?.default_language;
    if (def && def !== "en" && !localStorage.getItem("reason_lang_explicit") && prefs.language === "en") {
      update({ language: def });
    }
  }, [data, prefs.language, update]);

  // Issue 6 — apply the tenant's brand color app-wide by overriding the --primary token,
  // and remember it on this device so the login screen can re-brand for returning users.
  useEffect(() => {
    if (data?.is_custom) {
      applyBrandPrimary(data?.primary_color);
      try {
        localStorage.setItem(LOGIN_BRAND_KEY, JSON.stringify({
          company_name: data.company_name || "",
          logo_data_uri: data.logo_data_uri || "",
          primary_color: data.primary_color || "",
          show_logo: data.show_logo_reports !== false,
        }));
      } catch { /* ignore quota */ }
    } else {
      clearBrandPrimary();
      if (data && data.is_custom === false) {
        try { localStorage.removeItem(LOGIN_BRAND_KEY); } catch { /* noop */ }
      }
    }
    return () => clearBrandPrimary();
  }, [data]);

  return <BrandingCtx.Provider value={data || FALLBACK}>{children}</BrandingCtx.Provider>;
}

export function useBranding() {
  return useContext(BrandingCtx) || FALLBACK;
}
